import { BlurFade } from '@/components/ui/blur-fade';
import { AnimatedList } from '@/components/ui/animated-list';
import {
  Zap,
  UserCheck,
  AlertTriangle,
  Bell,
  Mail,
  Shield,
  Lock,
  Eye,
  Database,
  MessageSquare,
  Users,
  Wifi,
  MonitorSmartphone,
  BookOpen,
  Link2,
  ThumbsUp,
  Clock,
} from 'lucide-react';

const automationItems = [
  {
    icon: <UserCheck className="h-4 w-4 text-blue-500" />,
    text: 'Ticket assigned to Sarah M.',
    time: 'just now',
  },
  {
    icon: <AlertTriangle className="h-4 w-4 text-orange-500" />,
    text: 'Priority set to High',
    time: '2s ago',
  },
  {
    icon: <Bell className="h-4 w-4 text-purple-500" />,
    text: 'SLA timer started — 4h response',
    time: '3s ago',
  },
  {
    icon: <Mail className="h-4 w-4 text-green-500" />,
    text: 'Customer notified via email',
    time: '5s ago',
  },
  {
    icon: <Zap className="h-4 w-4 text-yellow-500" />,
    text: 'Category set to Billing',
    time: '6s ago',
  },
];

function AutomationNotification({ icon, text, time }: { icon: React.ReactNode; text: string; time: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 shadow-sm">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{text}</p>
        <p className="text-xs text-muted-foreground">{time}</p>
      </div>
    </div>
  );
}

const sections = [
  {
    title: 'Automate the Repetitive. Focus on What Matters.',
    description:
      'Build powerful automation rules without writing a single line of code. Trigger actions based on ticket events, conditions, and time-based schedules.',
    bullets: [
      'Time-based and event-driven triggers for tickets and articles',
      '16+ actions including assign, notify, email, and escalate',
      'Condition groups with AND/OR logic',
      'KB automation: content freshness alerts, feedback notifications, publish announcements',
    ],
    visual: 'automation',
    reverse: false,
  },
  {
    title: 'Knowledge Base That Works With Your Tickets',
    description:
      'More than a docs site. Link articles directly to tickets, gather customer feedback, and let automation keep your content fresh and your team informed.',
    bullets: [
      'Internal articles for agent-only runbooks and procedures',
      'Link articles to tickets as references or solutions',
      'Customer feedback with thumbs up/down and improvement comments',
      'Automated content freshness monitoring and publish notifications',
    ],
    visual: 'knowledgeBase',
    reverse: true,
  },
  {
    title: 'Collaborate Without Missing a Beat',
    description:
      'Keep your entire team in sync with real-time collaboration features that ensure no customer message goes unnoticed.',
    bullets: [
      'Live typing indicators between agents',
      'Agent presence and availability status',
      'Instant ticket updates across all devices',
      'Push notifications for mobile and desktop',
    ],
    visual: 'collaboration',
    reverse: true,
  },
  {
    title: 'Enterprise-Grade Security by Default',
    description:
      'Built from the ground up with multi-tenancy and data privacy in mind. Every query is tenant-isolated, every action is audited.',
    bullets: [
      'Complete multi-tenant data isolation',
      'Automated data retention policies',
      'PII cleanup and comprehensive audit trails',
      'Per-organization branding and configuration',
    ],
    visual: 'security',
    reverse: false,
  },
];

function CollaborationVisual() {
  const items = [
    { icon: <MessageSquare className="h-5 w-5" />, label: 'Live Comments' },
    { icon: <Users className="h-5 w-5" />, label: 'Agent Presence' },
    { icon: <Wifi className="h-5 w-5" />, label: 'Real-Time Sync' },
    { icon: <MonitorSmartphone className="h-5 w-5" />, label: 'Push Notifications' },
  ];

  return (
    <div className="grid grid-cols-2 gap-4">
      {items.map((item) => (
        <div
          key={item.label}
          className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="text-primary">{item.icon}</div>
          <span className="text-sm font-medium text-foreground">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function KnowledgeBaseVisual() {
  const items = [
    { icon: <BookOpen className="h-5 w-5" />, label: 'Internal Articles' },
    { icon: <Link2 className="h-5 w-5" />, label: 'Ticket Linking' },
    { icon: <ThumbsUp className="h-5 w-5" />, label: 'Article Feedback' },
    { icon: <Clock className="h-5 w-5" />, label: 'Freshness Alerts' },
  ];

  return (
    <div className="grid grid-cols-2 gap-4">
      {items.map((item) => (
        <div
          key={item.label}
          className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="text-primary">{item.icon}</div>
          <span className="text-sm font-medium text-foreground">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function SecurityVisual() {
  const items = [
    { icon: <Shield className="h-8 w-8" />, label: 'Tenant Isolation' },
    { icon: <Lock className="h-8 w-8" />, label: 'Encrypted Data' },
    { icon: <Eye className="h-8 w-8" />, label: 'Audit Logging' },
    { icon: <Database className="h-8 w-8" />, label: 'Data Retention' },
  ];

  return (
    <div className="grid grid-cols-2 gap-4">
      {items.map((item) => (
        <div
          key={item.label}
          className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="text-primary">{item.icon}</div>
          <span className="text-sm font-medium text-foreground">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

export function DetailedFeaturesSection() {
  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-20 lg:space-y-32">
        {sections.map((section) => (
          <BlurFade key={section.title} inView>
            <div
              className={`flex flex-col gap-8 sm:gap-12 items-center ${
                section.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'
              }`}
            >
              {/* Text side */}
              <div className="flex-1 space-y-4">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">
                  {section.title}
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base md:text-lg leading-relaxed">
                  {section.description}
                </p>
                <ul className="space-y-2 pt-2">
                  {section.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-2 text-muted-foreground">
                      <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Visual side */}
              <div className="flex-1 w-full max-w-md">
                {section.visual === 'automation' && (
                  <div className="relative h-[300px] overflow-hidden rounded-xl border border-border bg-card/50 p-4">
                    <AnimatedList delay={1500}>
                      {automationItems.map((item, i) => (
                        <AutomationNotification key={i} {...item} />
                      ))}
                    </AnimatedList>
                  </div>
                )}
                {section.visual === 'collaboration' && <CollaborationVisual />}
                {section.visual === 'knowledgeBase' && <KnowledgeBaseVisual />}
                {section.visual === 'security' && <SecurityVisual />}
              </div>
            </div>
          </BlurFade>
        ))}
      </div>
    </section>
  );
}
